// ─── Knowledge Report Route ───
// /app/knowledge — docs/design/components/conocimientos-matrix.md §1 (Matrix
// tab) + docs/design/components/conocimientos-strengths.md §1 (the Team
// Strengths tab added beside it). The cross-people knowledge reporting
// surface: managers + superadmin only. This route re-checks `isAnyManager`
// at component-render time (defense-in-depth / UX polish — the API's own
// `assertAnyManager`, ADR 0010, is the real security boundary, already
// shipped and unconditional) and redirects a non-manager who deep-links in
// directly, using the exact `<Navigate>` pattern `AppLayout` already uses for
// the unauthenticated case (routes/app.tsx) — this codebase has no
// router-`beforeLoad`-with-auth-context precedent (§1.3).
//
// View state (questionnaireId/workspaceId/questionId/minLevel/userId/view/
// coverageThreshold) lives in the URL — §1.3's deliberate divergence from PR
// 5/PR 6's "no deep link" Settings-tab ruling (that ruling was specific to
// not touching the SHARED `settingsSearchSchema`; this route owns its own
// schema, so there's no shared file to protect). Closest real precedent:
// Dashboards' `useDashboardViewParams.ts` (`ws`/`space`/`company`/`from`/`to`
// all live in the URL) — see `features/knowledge/useKnowledgeMatrixParams.ts`.
//
// `.catch(undefined)` on every field (a small, deliberate addition beyond
// the spec's own literal pseudocode, matching `routes/app/calendar-year.tsx`'s
// `ask` param and `routes/app.tsx`'s dashboard canvas schema): a garbage/
// stale deep link (e.g. `?minLevel=notanumber`) must degrade to "param
// absent," never throw and break the whole route.

import { createFileRoute, Navigate } from '@tanstack/react-router';
import { z } from 'zod';
import { useAuth } from '../../features/auth/AuthProvider';
import { isAnyManager } from '../../hooks/useIsAnyManager';
import { KnowledgeReportPage } from '../../features/knowledge/KnowledgeReportPage';

// #482 retired the `questionId`/`minLevel` pair (and its pair-or-neither
// transform): the per-question filter is gone from the surface AND the API.
// Zod strips unknown query keys, so a stale deep link still carrying them
// simply loses them — never a crash, never a 400.
export const knowledgeSearchSchema = z.object({
  /**
   * Which knowledge form is being viewed — a FRIENDLY REF (`{slug}-{short}`, ADR 0012) since
   * #551, e.g. `conocimientos-backend-2026-878e8e`, because a raw UUID made this the ugliest
   * shareable URL in the app.
   *
   * Still accepts a bare UUID, so every already-shared link keeps resolving; the shell
   * canonicalises it to the friendly form on load (`replace: true`, no history entry).
   * Resolution is CLIENT-SIDE against the questionnaire list the selector already fetches —
   * no `/resolve` endpoint, no extra round trip.
   *
   * `.min(1)` rather than `.uuid()` for exactly that reason; the bound keeps a hand-crafted
   * value from being unbounded.
   */
  questionnaireId: z.string().min(1).max(120).optional().catch(undefined),
  /** The department half of the scope filter (§4.1). Shared by both tabs
   *  (conocimientos-strengths.md §1.5). */
  workspaceId: z.string().uuid().optional().catch(undefined),
  /** The space half (#482) — narrows to who BELONGS to the space (its team ∪
   *  its grantees). Always set together with `workspaceId` by the scope
   *  picker; the server guards the pair, so a hand-crafted mismatch just
   *  yields an empty (no-leak) result. */
  /**
   * GHE #835 — three states, not two: absent = "not chosen yet" (defaults to the space you are
   * in), `'all'` = explicitly every space, a uuid = that space. A bare `z.string().uuid()` here
   * would drop `'all'` through `.catch(undefined)` and silently turn an explicit "All spaces"
   * back into "not chosen", which is the exact bug the sentinel exists to prevent.
   */
  spaceId: z
    .union([z.literal('all'), z.string().uuid()])
    .optional()
    .catch(undefined),
  /** Opens the drill-down panel (§5) for this person on load — a deep link INTO a row. */
  userId: z.string().uuid().optional().catch(undefined),
  /**
   * #551 — an EXPLICIT override of the "only people who filled the form" default.
   *
   * Absent (the common case) means "derive it": on the Matrix tab the filter is ON while no
   * scope is chosen and OFF once a workspace/space is, per the owner's ruling. A literal
   * 'true'/'false' pins it, so a shared link shows what the sender was looking at.
   *
   * 🚨 Accepts a BOOLEAN as well as the string literals, and that is not belt-and-braces:
   * TanStack's search parser JSON-parses values, so a typed or shared `?respondedOnly=true`
   * arrives here as a real `boolean`, exactly as `?ganttWeeks=8` arrives as a number (#437).
   * A string-only schema would reject it and `.catch(undefined)` would silently DROP the
   * flag — the link would look right and do nothing.
   *
   * Never `z.coerce.boolean()`: `Boolean('false') === true`, which would make
   * `?respondedOnly=false` mean its own opposite (#401's defect, twice shipped).
   */
  respondedOnly: z
    .union([z.boolean(), z.enum(['true', 'false'])])
    .transform((v) => v === true || v === 'true')
    .optional()
    .catch(undefined),
  /** Which top-level tab is active (conocimientos-strengths.md §1.3/§1.5) —
   *  absent = 'matrix', so every pre-existing matrix deep link keeps working
   *  unchanged. */
  view: z.enum(['matrix', 'strengths']).optional().catch(undefined),
  /** The Team Strengths coverage-proficiency threshold (§2.3). Strengths-only. */
  coverageThreshold: z.coerce.number().int().min(0).max(4).optional().catch(undefined),
});

export const Route = createFileRoute('/app/knowledge')({
  validateSearch: knowledgeSearchSchema,
  component: KnowledgeMatrixRouteGuard,
});

// GHE #283 item 2 added a single-segment Breadcrumb here on the grounds that it
// was "the one consistent place every route now shows 'where am I'". GHE #452
// measured that claim and it was never true — the breadcrumb reached 4 of the 14
// in-app pages, and here the lone segment rendered "Conocimientos del equipo"
// immediately above KnowledgeReportPage's identical <h1>. Breadcrumb now declines
// to render below 2 segments, so this route shows none.
function KnowledgeMatrixRouteGuard(): React.ReactElement {
  const { user } = useAuth();
  if (!isAnyManager(user)) return <Navigate to="/app" replace />;
  return <KnowledgeReportPage />;
}
